import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { Users, Target, Award, TrendingUp, CheckCircle } from 'lucide-react';

const AboutUsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (user) {
      navigate('/services');
    } else {
      navigate('/register');
    }
  };
  const values = [
    {
      icon: Target,
      title: 'Our Mission',
      description: 'To simplify tax compliance and make professional financial services accessible to everyone in India.'
    },
    {
      icon: Users,
      title: 'Expert Team',
      description: 'Qualified Chartered Accountants with years of experience in taxation and business consulting.'
    },
    {
      icon: Award,
      title: 'Quality Service',
      description: 'Committed to delivering accurate, timely, and reliable tax filing and business registration services.'
    },
    {
      icon: TrendingUp,
      title: 'Growth Focus',
      description: 'Helping individuals and businesses grow by providing strategic tax planning and compliance solutions.'
    }
  ];

  const stats = [
    { number: '5000+', label: 'Happy Clients' },
    { number: '10+', label: 'Years Experience' },
    { number: '15000+', label: 'Returns Filed' },
    { number: '99%', label: 'Success Rate' }
  ];

  const whyChooseUs = [
    'Expert CA review on every return',
    'Quick 24-48 hour processing',
    'Secure document storage',
    'Transparent pricing with no hidden charges',
    'Dedicated customer support',
    'Compliance with latest tax regulations'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-20" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-5xl font-bold mb-6" data-testid="about-title">About ITRQUICK</h1>
          <p className="text-xl text-blue-100 max-w-3xl" data-testid="about-subtitle">
            Your trusted partner for tax filing, GST services, and business registration in India. 
            We combine expertise with technology to deliver seamless financial compliance solutions.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 px-4" data-testid="our-story-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Our Story</h2>
              <p className="text-gray-700 mb-4">
                Founded with a vision to simplify tax compliance for every Indian, ITRQUICK has grown to become 
                a trusted name in online tax filing and business services.
              </p>
              <p className="text-gray-700 mb-4">
                We understand that tax filing can be complex and time-consuming. That's why we've built a platform 
                that combines the expertise of qualified Chartered Accountants with cutting-edge technology to make 
                the entire process smooth and hassle-free.
              </p>
              <p className="text-gray-700">
                Today, we serve thousands of individuals and businesses across India, helping them stay compliant 
                while focusing on what matters most – their growth and success.
              </p>
            </div>
            <div className="bg-blue-50 p-8 rounded-xl">
              <div className="grid grid-cols-2 gap-6">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    <div className="text-4xl font-bold text-blue-600 mb-2">{stat.number}</div>
                    <div className="text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 px-4 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">What We Stand For</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value) => (
              <div key={value.title} className="text-center" data-testid={`value-${value.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                  <value.icon className="text-blue-600" size={32} />
                </div>
                <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 px-4" data-testid="why-choose-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Why Choose ITRQUICK?</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {whyChooseUs.map((reason) => (
              <div key={reason} className="flex items-start gap-3" data-testid={`reason-${reason.toLowerCase().substring(0, 20)}`}>
                <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={24} />
                <p className="text-gray-700 text-lg">{reason}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-blue-600 text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of satisfied customers who trust ITRQUICK for their tax and business needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleGetStarted}
              className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition" 
              data-testid="get-started-cta"
            >
              Get Started Now
            </button>
            <a href="/contact" className="bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold border-2 border-white hover:bg-blue-800 transition" data-testid="contact-cta">
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUsPage;